import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  X, MessageSquare, Bell, Heart, BellRing, Clock, ShoppingBag, Gavel, Package,
  Tag, CheckCircle2, Layers, CreditCard, UserCog, LogOut, DollarSign, Plus, Shield,
} from "lucide-react";
import { useCurrency } from "@/lib/currency";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { checkIsAdmin } from "@/lib/is-admin.functions";



interface Item {
  icon: typeof Heart;
  label: string;
  href: string;
  routed?: boolean;
  badge?: number;
}

export function ProfileDrawer({ open, onClose, username }: { open: boolean; onClose: () => void; username: string | null }) {
  const { display, setDisplay, available, source, upstreamUpdatedAt, stale } = useCurrency();

  const fxLabel = (() => {
    if (stale || source === "fallback") return "Using cached fallback rates (live rate service unreachable)";
    const name = source === "frankfurter" ? "Frankfurter (ECB)" : "ExchangeRate-API";
    if (!upstreamUpdatedAt) return `Rates from ${name}`;
    const diff = Date.now() - new Date(upstreamUpdatedAt).getTime();
    const mins = Math.max(0, Math.floor(diff / 60000));
    const rel = mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;
    return `Rates from ${name} · updated ${rel}`;
  })();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const checkIsAdminFn = useServerFn(checkIsAdmin);
  const { data: adminCheck } = useQuery({
    queryKey: ["is-admin", user?.id ?? null],
    queryFn: () => checkIsAdminFn(),
    enabled: open && !!user,
    staleTime: 5 * 60 * 1000,
  });
  const isAdmin = !!adminCheck?.isAdmin;

  // Badge counts default to 0 until live counts wire into the drawer.
  const unreadMsgs = 0;
  const unreadNotifs = 0;

  async function handleSignOut() {
    try {
      await queryClient.cancelQueries();
      queryClient.clear();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      onClose();
      navigate({ to: "/auth", replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sign out failed");
    }
  }

  const SECTIONS: { label?: string; items: Item[] }[] = [
    {

      items: [],
    },
    {
      label: "Activity",
      items: [
        { icon: MessageSquare, label: "Messages", href: "/messages", routed: true, badge: unreadMsgs },
        { icon: Bell, label: "Notifications", href: "/notifications", routed: true, badge: unreadNotifs },
      ],
    },
    {
      label: "Buying",
      items: [
        { icon: Clock, label: "Recently Viewed", href: "/recently-viewed", routed: true },
        { icon: ShoppingBag, label: "Buy Orders", href: "/dashboard", routed: true },
        { icon: Gavel, label: "Bids and Offers", href: "/bids", routed: true },
        { icon: Package, label: "Purchases", href: "/purchases", routed: true },
      ],
    },
    {
      label: "Selling",
      items: [
        { icon: Heart, label: "Watchlist", href: "/wishlist", routed: true },
        { icon: BellRing, label: "Price Alerts", href: "/price-alerts", routed: true },
        { icon: Tag, label: "My Sale Offers", href: "/my-listings", routed: true },
        { icon: CheckCircle2, label: "Sold Items", href: "/sold-items", routed: true },
        { icon: Layers, label: "Collection", href: "/collection", routed: true },
      ],
    },
    {
      label: "Account",
      items: [
        { icon: UserCog, label: "Account Settings", href: "/account", routed: true },
      ],
    },

  ];

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  // Light: white bg, navy text, light grey hover. Dark: dark navy bg, white text.
  const itemClass =
    "flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-[#0A2342] hover:bg-[#F5F7FA] transition dark:text-white/85 dark:hover:bg-white/[0.06] dark:hover:text-white";

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        aria-hidden="true"
      />
      <aside
        className={`fixed right-0 top-0 z-50 flex h-screen w-[22rem] max-w-[92vw] transform flex-col border-l border-[#E2E8F0] bg-white text-[#0A2342] shadow-2xl transition-[transform,background-color,color,border-color] duration-300 ease-out dark:border-white/10 dark:bg-[#16213E] dark:text-white ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#E2E8F0] px-5 py-4 dark:border-white/10">
          <Link
            to="/profile/$username"
            params={{ username: username ?? "" }}
            onClick={onClose}
            className="flex items-center gap-3 group"
          >
            <div className="grid h-10 w-10 place-items-center rounded-full bg-[#7B2FBE] font-display font-bold text-white dark:bg-primary dark:text-primary-foreground">
              {(username?.[0] ?? "?").toUpperCase()}
            </div>
            <div>
              <div className="font-display text-sm font-bold group-hover:text-[#7B2FBE] dark:group-hover:text-primary">@{username ?? "…"}</div>
              <div className="text-[11px] text-[#718096] dark:text-white/60">View profile</div>
            </div>
          </Link>
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="grid h-9 w-9 place-items-center rounded-md text-[#718096] hover:bg-[#F5F7FA] hover:text-[#0A2342] dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scroll area */}
        <nav className="flex-1 overflow-y-auto py-3">
          {/* Section 1 — Prominent CTA */}
          <div className="px-4 pb-3">
            <Link
              to="/create-order"
              onClick={onClose}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-[#7B2FBE] px-4 py-2.5 text-sm font-bold text-white shadow transition hover:opacity-90 dark:bg-primary dark:text-primary-foreground"
            >
              <Plus className="h-4 w-4" /> Post Buy Order
            </Link>
          </div>

          {SECTIONS.slice(1).map((sec) => (
            <div key={sec.label} className="border-t border-[#E2E8F0] pt-2 pb-1 dark:border-white/10">
              {sec.label && (
                <div className="px-5 pb-1 pt-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#718096] dark:text-white/45">
                  {sec.label}
                </div>
              )}
              {sec.items.map((it) => {
                const Icon = it.icon;
                const content = (
                  <>
                    <Icon className="h-4 w-4 text-[#718096] dark:text-white/70" />
                    <span className="flex-1">{it.label}</span>
                    {it.badge ? (
                      <span className="grid min-w-[1.25rem] place-items-center rounded-full bg-[#7B2FBE] px-1.5 text-[10px] font-bold text-white dark:bg-primary dark:text-primary-foreground">
                        {it.badge}
                      </span>
                    ) : null}
                  </>
                );
                return it.routed ? (
                  <Link key={it.label} to={it.href as any} onClick={onClose} className={itemClass}>
                    {content}
                  </Link>
                ) : (
                  <a key={it.label} href={it.href} onClick={onClose} className={itemClass}>
                    {content}
                  </a>
                );
              })}
            </div>
          ))}

          {isAdmin && (
            <div className="border-t border-[#E2E8F0] pt-2 pb-1 dark:border-white/10">
              <div className="px-5 pb-1 pt-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#718096] dark:text-white/45">
                Admin
              </div>
              <Link to="/wtb-ops-control" onClick={onClose} className={itemClass}>
                <Shield className="h-4 w-4 text-[#718096] dark:text-white/70" />
                <span className="flex-1">Ops Console</span>
              </Link>
            </div>
          )}
        </nav>

        {/* Footer: currency + logout */}
        <div className="border-t border-[#E2E8F0] px-5 py-4 dark:border-white/10">
          <label className="flex items-center justify-between gap-3 text-sm font-medium text-[#0A2342] dark:text-white/85">
            <span className="flex items-center gap-3">
              <DollarSign className="h-4 w-4 text-[#718096] dark:text-white/70" />
              Display currency
            </span>
            <select
              value={display}
              onChange={(e) => setDisplay(e.target.value)}
              className="h-8 rounded-md border border-[#E2E8F0] bg-white px-2 text-xs font-semibold text-[#0A2342] focus:border-[#7B2FBE] focus:outline-none dark:border-white/15 dark:bg-white/10 dark:text-white dark:focus:border-primary"
            >
              {available.map((c) => (
                <option key={c} value={c} className="bg-white text-[#0A2342] dark:bg-[#16213E] dark:text-white">{c}</option>
              ))}
            </select>
          </label>
          <p className="mt-1.5 text-[11px] text-[#718096] dark:text-white/50">Display only — buyer and seller arrange payment directly.</p>
          <p className={`mt-0.5 text-[10px] ${stale || source === "fallback" ? "text-amber-600 dark:text-amber-400" : "text-[#718096] dark:text-white/40"}`}>{fxLabel}</p>
        </div>
        <div className="border-t border-[#E2E8F0] dark:border-white/10">
          <button
            type="button"
            onClick={handleSignOut}
            className="flex w-full items-center gap-3 px-5 py-3 text-left text-sm font-medium text-[#E53E3E] transition hover:bg-[#E53E3E]/10 dark:text-destructive dark:hover:bg-destructive/10"
          >
            <LogOut className="h-4 w-4" />
            <span>Log out</span>
          </button>

        </div>
      </aside>
    </>
  );
}
